import { useState, useEffect } from "react";
import { Package, ShoppingCart, Users, AlertTriangle, TrendingUp, DollarSign } from "lucide-react";
import axios from "axios";
import AdminLayout from "../../components/AdminLayout";
import { API } from "../../App";
import { motion } from "framer-motion";

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/dashboard`, { withCredentials: true });
        setStats(response.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <AdminLayout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-card rounded"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-32 bg-card rounded-xl"></div>
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  const statCards = [
    { 
      label: 'Total Revenue', 
      value: `₹${stats?.total_revenue?.toLocaleString() || 0}`, 
      icon: DollarSign, 
      color: 'gold',
      change: '+12%'
    },
    { 
      label: 'Total Orders', 
      value: stats?.total_orders || 0, 
      icon: ShoppingCart, 
      color: 'blue',
      change: '+8%'
    },
    { 
      label: 'Customers', 
      value: stats?.customer_count || 0, 
      icon: Users, 
      color: 'green',
      change: '+15%'
    },
    { 
      label: 'Low Stock Items', 
      value: stats?.low_stock_count || 0, 
      icon: AlertTriangle, 
      color: stats?.low_stock_count > 0 ? '' : 'green',
      alert: stats?.low_stock_count > 0
    },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">Overview of your store performance</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`stat-card ${stat.color}`}
              data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-2xl font-bold text-foreground mt-1">{stat.value}</p>
                  {stat.change && (
                    <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {stat.change} from last month
                    </p>
                  )}
                </div>
                <div className={`p-3 rounded-lg ${stat.alert ? 'bg-red-500/10' : 'bg-white/5'}`}>
                  <stat.icon className={`w-5 h-5 ${stat.alert ? 'text-red-500' : 'text-secondary'}`} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Products Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-card border border-white/5 rounded-xl overflow-hidden"
        >
          <div className="p-6 border-b border-white/5">
            <h2 className="text-lg font-semibold text-foreground">Product Inventory</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Product</th>
                  <th>Weight</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {stats?.products?.map((product) => (
                  <tr key={product.product_id} data-testid={`product-row-${product.sku}`}>
                    <td className="font-mono text-sm text-muted-foreground">{product.sku}</td>
                    <td className="font-medium text-foreground">{product.name}</td>
                    <td>{product.weight >= 1000 ? `${product.weight/1000}kg` : `${product.weight}g`}</td>
                    <td>₹{product.price}</td>
                    <td className={product.stock <= product.low_stock_threshold ? 'text-red-500' : 'text-foreground'}>
                      {product.stock}
                    </td>
                    <td>
                      {product.stock === 0 ? (
                        <span className="px-2 py-1 bg-red-500/10 text-red-500 text-xs rounded">Out of Stock</span>
                      ) : product.stock <= product.low_stock_threshold ? (
                        <span className="px-2 py-1 bg-yellow-500/10 text-yellow-500 text-xs rounded">Low Stock</span>
                      ) : (
                        <span className="px-2 py-1 bg-green-500/10 text-green-500 text-xs rounded">In Stock</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Pending Orders Alert */}
        {stats?.pending_orders > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl flex items-center gap-4"
          >
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            <p className="text-sm text-foreground">
              You have <span className="font-bold">{stats.pending_orders}</span> pending order(s) that need attention.
            </p>
          </motion.div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
